import type { PlayerPreferences, SoundtrackLibrary, SoundtrackTrack, SoundtrackCategory } from './player/types';
import type {
  ExportStatus,
  DriveLinkInfo,
  Episode,
  EpisodeSetupResponse,
  EpisodePeopleResponse,
  PersonSummary,
  Relation,
  SetupSuggestions,
  StorySetup,
  StoryResponse,
  NarrationStatus,
  GoogleStatus,
  HomeResponse,
  ImportItem,
  ImportLimits,
  PickerToken,
  MediaItem,
  MediaListResponse,
  MediaStats,
  Season,
  UploadResponse,
  AcceptedInvite,
  AuthUser,
  Share,
  ShareRole,
  ShareScope,
  SharedWithMe,
  InvitePreview,
  SeasonAccess,
} from './types';

/** Sent on every call; the server requires it for credential endpoints (CSRF). */
const CLIENT_HEADER = { 'X-Memoryflix-Client': '1' };

/** Fired when the server says the session is gone (see auth.tsx). */
export const SIGNED_OUT_EVENT = 'memoryflix:signed-out';

/** `?owner=` for things in a library someone shared with the user (none = own library). */
const ownerQuery = (owner?: string | null) => (owner ? `?owner=${encodeURIComponent(owner)}` : '');

/** An API error that keeps the HTTP status and the server's error code. */
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
  ) {
    super(message);
  }
}

/**
 * All API traffic goes through here so error handling is consistent.
 * In dev, Vite proxies /api and /media to the server, so we can always use
 * same-origin relative URLs.
 */
async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      ...CLIENT_HEADER,
      ...(init?.body && !(init.body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  });

  if (!res.ok) {
    // Session ended (signed out elsewhere, expired): the app shows the sign-in screen.
    if (res.status === 401 && !path.startsWith('/api/auth/')) window.dispatchEvent(new Event(SIGNED_OUT_EVENT));
    let message = `Request failed (${res.status})`;
    let code: string | undefined;
    try {
      const body = (await res.json()) as { error?: string; code?: string };
      if (body.error) message = body.error;
      code = body.code;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(message, res.status, code);
  }
  return (await res.json()) as T;
}

export interface NarrationVoice {
  id: string;
  name: string;
  gender: string;
  description: string;
}

export const api = {
  home: () => request<HomeResponse>('/api/home'),

  seasons: () => request<Season[]>('/api/seasons'),

  season: (year: number, owner?: string | null) =>
    request<Season & { episodes: Episode[]; access?: SeasonAccess }>(`/api/seasons/${year}${ownerQuery(owner)}`),

  episodes: (year?: number) =>
    request<Episode[]>(`/api/episodes${year ? `?year=${year}` : ''}`),

  episode: (id: string) => request<Episode>(`/api/episodes/${id}`),

  episodeMedia: (id: string) =>
    request<{ episodeId: string; title: string; items: MediaItem[] }>(
      `/api/episodes/${id}/media`,
    ),

  updateEpisode: (
    id: string,
    patch: { title?: string; description?: string; coverMediaId?: string | null; userSequence?: number | null },
  ) =>
    request<Episode>(`/api/episodes/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  restoreEpisode: (id: string, fields: Array<'title' | 'description' | 'cover' | 'order'>) =>
    request<Episode>(`/api/episodes/${id}/restore`, {
      method: 'POST',
      body: JSON.stringify({ fields }),
    }),

  /** "Make an episode" from memories the user picked (all must be processed). */
  composeEpisode: (mediaIds: string[]) =>
    request<Episode>('/api/episodes/compose', { method: 'POST', body: JSON.stringify({ mediaIds }) }),

  /** "Make it a story": read, write (takes ~15-30 s), or remove the AI lines. */
  story: (id: string) => request<StoryResponse>(`/api/episodes/${id}/story`),
  makeStory: (id: string) => request<StoryResponse>(`/api/episodes/${id}/story`, { method: 'POST' }),
  /** Save the user's own edits; `lines` is the full list as the user wants it. */
  editStory: (
    id: string,
    patch: {
      opening?: { en: string; ur: string };
      closing?: { en: string; ur: string };
      lines?: Array<{ mediaId: string; en: string; ur: string }>;
      /** The photos left out of the story (the full list). */
      skipped?: string[];
    },
  ) => request<StoryResponse>(`/api/episodes/${id}/story`, { method: 'PATCH', body: JSON.stringify(patch) }),
  /** Take a photo out of the episode (it goes back to the library; the file is kept). */
  removeEpisodeMedia: (episodeId: string, mediaId: string) =>
    request<{ ok: true; episodeId: string }>(`/api/episodes/${episodeId}/media/${mediaId}`, { method: 'DELETE' }),
  deleteStory: (id: string) => request<StoryResponse>(`/api/episodes/${id}/story`, { method: 'DELETE' }),

  /** Faces in an episode grouped by person (found on this device). */
  episodePeople: (id: string) => request<EpisodePeopleResponse>(`/api/episodes/${id}/people`),
  people: (owner?: string | null) =>
    request<{ people: PersonSummary[]; relations: Relation[] }>(`/api/people${ownerQuery(owner)}`),
  /** Name faces as an existing or new person, hide them, or reject a suggestion. */
  assignFaces: (
    body: {
      faceIds: string[];
      personId?: string;
      newPerson?: { name: string; relation?: Relation | null };
      action?: 'hide' | 'reject';
    },
    owner?: string | null,
  ) =>
    request<{ personId: string | null }>(`/api/people/faces/assign${ownerQuery(owner)}`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updatePerson: (id: string, patch: { name?: string; relation?: Relation | null }, owner?: string | null) =>
    request<{ ok: true }>(`/api/people/${id}${ownerQuery(owner)}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  deletePerson: (id: string, owner?: string | null) =>
    request<{ ok: true }>(`/api/people/${id}${ownerQuery(owner)}`, { method: 'DELETE' }),
  faceImageUrl: (faceId: string, owner?: string | null) => `/api/people/faces/${faceId}/image${ownerQuery(owner)}`,

  /** Voice narration: whether it is set up on the server, and the account's voices. */
  narrationVoices: () =>
    request<{
      enabled: boolean;
      model: string;
      defaultVoiceId: string | null;
      urduVoices: Array<{ id: string; name: string; gender: string }>;
      voices: NarrationVoice[];
    }>(
      '/api/narration/voices',
    ),

  /** Narration clips of a story in both languages, their lengths, and the readiness checks. */
  narrationStatus: (id: string) => request<NarrationStatus>(`/api/narration/${id}/status`),
  /** Download video: where it stands (progress, the current file). */
  exportStatus: (id: string) => request<ExportStatus>(`/api/episodes/${id}/export`),
  /** Make the video in this language (replaces the episode's earlier one). Returns at once. */
  startExport: (id: string, lang: 'en' | 'ur') =>
    request<ExportStatus>(`/api/episodes/${id}/export`, { method: 'POST', body: JSON.stringify({ lang }) }),
  /** Download address of the video. */
  exportFileUrl: (id: string) => `/api/episodes/${id}/export/file`,
  /** Make every missing clip (English and Urdu). Returns at once; follow it with narrationStatus. */
  prepareNarration: (id: string) => request<NarrationStatus>(`/api/narration/${id}/prepare`, { method: 'POST' }),

  episodeSetup: (id: string) => request<EpisodeSetupResponse>(`/api/episodes/${id}/setup`),

  saveEpisodeSetup: (
    id: string,
    patch: Partial<Omit<StorySetup, 'completedAt'>> & { completed?: boolean },
  ) =>
    request<{ episodeId: string; setup: StorySetup; suggested: SetupSuggestions }>(`/api/episodes/${id}/setup`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  renameSeason: (year: number, title: string, owner?: string | null) =>
    request<Season>(`/api/seasons/${year}${ownerQuery(owner)}`, { method: 'PATCH', body: JSON.stringify({ title }) }),

  restoreSeason: (year: number, owner?: string | null) =>
    request<Season>(`/api/seasons/${year}/restore${ownerQuery(owner)}`, { method: 'POST' }),

  importLimits: () => request<ImportLimits>('/api/imports/limits'),

  playerPreferences: () => request<PlayerPreferences>('/api/player/preferences'),

  savePlayerPreferences: (patch: Partial<PlayerPreferences>) =>
    request<PlayerPreferences>('/api/player/preferences', { method: 'PUT', body: JSON.stringify(patch) }),

  soundtracks: () => request<SoundtrackLibrary>('/api/player/soundtracks'),

  addSoundtracks: (files: File[], category: SoundtrackCategory) => {
    const form = new FormData();
    for (const f of files) form.append('files', f);
    form.append('category', category);
    return request<{
      results: Array<{ filename: string; status: 'added' | 'duplicate' | 'rejected'; error?: string; track?: SoundtrackTrack }>;
    }>('/api/player/soundtracks', { method: 'POST', body: form });
  },

  updateSoundtrack: (id: string, patch: { title?: string; category?: SoundtrackCategory }) =>
    request<SoundtrackTrack>(`/api/player/soundtracks/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),

  deleteSoundtrack: (id: string) => request<{ ok: boolean }>(`/api/player/soundtracks/${id}`, { method: 'DELETE' }),

  imports: (params: { batchId?: string; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.batchId) q.set('batchId', params.batchId);
    if (params.limit) q.set('limit', String(params.limit));
    return request<{ items: ImportItem[] }>(`/api/imports?${q.toString()}`);
  },

  retryImport: (id: string) => request<{ ok: boolean }>(`/api/imports/${id}/retry`, { method: 'POST' }),

  googleStatus: () => request<GoogleStatus>('/api/google/status'),

  googlePickerToken: () => request<PickerToken>('/api/google/picker-token', { method: 'POST' }),

  googleDisconnect: () => request<{ ok: boolean }>('/api/google/disconnect', { method: 'POST' }),

  googleImport: (files: Array<{ id: string; resourceKey?: string; name?: string }>) =>
    request<{ batchId: string; count: number }>('/api/google/import', {
      method: 'POST',
      body: JSON.stringify({ files }),
    }),

  inspectDriveLink: (url: string) =>
    request<DriveLinkInfo>('/api/google/inspect-link', { method: 'POST', body: JSON.stringify({ url }) }),

  media: (params: { status?: string; type?: string; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.status) q.set('status', params.status);
    if (params.type) q.set('type', params.type);
    if (params.limit) q.set('limit', String(params.limit));
    const qs = q.toString();
    return request<MediaListResponse>(`/api/media${qs ? `?${qs}` : ''}`);
  },

  mediaStats: () => request<MediaStats>('/api/media/stats'),
  /** Deletes Memoryflix's copies (originals elsewhere are untouched); episodes are rebuilt. */
  deleteMedia: (ids: string[]) =>
    request<{ ok: true; deleted: number }>('/api/media/delete', { method: 'POST', body: JSON.stringify({ ids }) }),

  /**
   * Upload one request's worth of files. `lastModified` travels alongside so
   * a photo without embedded metadata is dated by the device's own file time
   * rather than the moment of upload. `onProgress` reports bytes sent.
   */
  upload: (files: File[], opts: { batchId?: string; onProgress?: (loaded: number) => void } = {}) =>
    new Promise<UploadResponse>((resolve, reject) => {
      const form = new FormData();
      for (const file of files) form.append('files', file);
      form.append('meta', JSON.stringify(files.map((f) => ({ lastModified: f.lastModified || undefined }))));
      if (opts.batchId) form.append('batchId', opts.batchId);
      const onProgress = opts.onProgress;

      // XHR rather than fetch: upload progress events are not available on
      // fetch in any shipping browser.
      const xhr = new XMLHttpRequest();
      xhr.open('POST', '/api/media/upload');
      xhr.setRequestHeader('X-Memoryflix-Client', '1');
      xhr.upload.onprogress = (e) => {
        if (onProgress) onProgress(e.loaded);
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(JSON.parse(xhr.responseText) as UploadResponse);
        } else {
          let message = `Upload failed (${xhr.status})`;
          try {
            message = (JSON.parse(xhr.responseText) as { error?: string }).error ?? message;
          } catch { /* ignore */ }
          reject(new ApiError(message, xhr.status));
        }
      };
      xhr.onerror = () => reject(new Error('Network error during upload'));
      xhr.send(form);
    }),

  rebuild: () =>
    request<{
      mediaScanned: number;
      eventsCreated: number;
      episodesCreated: number;
      seasonsCreated: number;
      durationMs: number;
    }>('/api/rebuild-timeline', { method: 'POST' }),

  saveProgress: (episodeId: string, positionSeconds: number, completed = false) =>
    request<{ ok: boolean }>('/api/home/watch-progress', {
      method: 'POST',
      body: JSON.stringify({ episodeId, positionSeconds, completed }),
    }),

  // ---- Accounts ----
  me: () => request<{ user: AuthUser }>('/api/auth/me'),
  login: (email: string, password: string) =>
    request<{ user: AuthUser }>('/api/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  register: (body: { name: string; email: string; password: string; invite?: string }) =>
    request<{ user: AuthUser; accepted: AcceptedInvite | null }>('/api/auth/register', { method: 'POST', body: JSON.stringify(body) }),
  logout: () => request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),

  // ---- Sharing ----
  shares: (episodeId?: string) =>
    request<{ shares: Share[] }>(`/api/shares${episodeId ? `?episodeId=${encodeURIComponent(episodeId)}` : ''}`),
  createShare: (body: {
    scope: ShareScope;
    role: ShareRole;
    episodeId?: string;
    seasonYear?: number;
    email?: string;
  }) => request<Share>('/api/shares', { method: 'POST', body: JSON.stringify(body) }),
  updateShare: (id: string, role: ShareRole) =>
    request<Share>(`/api/shares/${id}`, { method: 'PATCH', body: JSON.stringify({ role }) }),
  deleteShare: (id: string) => request<{ ok: true }>(`/api/shares/${id}`, { method: 'DELETE' }),
  sharedWithMe: () => request<{ shares: SharedWithMe[] }>('/api/shares/with-me'),
  invite: (token: string) => request<InvitePreview>(`/api/invites/${encodeURIComponent(token)}`),
  acceptInvite: (token: string) =>
    request<AcceptedInvite>(
      `/api/invites/${encodeURIComponent(token)}/accept`,
      { method: 'POST' },
    ),
};